// Audit JSON shape returned by POST /public/audit/pdp.
// Mirrors PDP_AUDIT_RESPONSE_SCHEMA on the backend
// (desirelist-api/application/src/lib/controllers/pdpAuditPrompt.js).

export interface AuditSection {
    score: string; // e.g. "14/20"
    strengths: string;
    gaps: string;
}

export interface PdpAuditScores {
    humanConversion: number;
    agenticCommerce: number;
    overall: number;
}

export interface PdpAuditKeyFindings {
    strengths: string[];
    opportunities: string[];
}

export interface PdpAuditHumanConversion {
    conversionOptimization: AuditSection;
    contentQuality: AuditSection;
    visualExperience: AuditSection;
    socialProof: AuditSection;
    seoMetadata: AuditSection;
    technicalPerformance: AuditSection;
    mobileFirst: AuditSection;
    competitorBenchmarking: AuditSection;
}

export interface PdpAuditAgenticCommerce {
    structuredTruth: AuditSection;
    semanticClarity: AuditSection;
    explainability: AuditSection;
    multimodalGrounding: AuditSection;
    executionConfidenceSignals: AuditSection;
    machineCommerceSignals: AuditSection;
}

export interface PdpAudit {
    brandName: string;
    productName: string;
    scores: PdpAuditScores;
    keyFindings: PdpAuditKeyFindings;
    humanConversion: PdpAuditHumanConversion;
    agenticCommerce: PdpAuditAgenticCommerce;
}

export interface PdpAuditMeta {
    url: string;
    finalUrl: string;
    title: string;
    metaDescription: string;
    jsonLdBlockCount: number;
    geminiStatus:
        | "success"
        | "error"
        | "no_api_key"
        | "rate_limited"
        | string;
    geminiStatusCode: number | null;
    geminiError: string | null;
    // Persisted pdp_audit_reports row id — carried into the proposal wizard
    // so the resulting proposal links back to this audit.
    reportId?: number | null;
}

export interface PdpAuditResponse {
    audit: PdpAudit;
    meta: PdpAuditMeta;
}

export const HUMAN_CONVERSION_SECTION_LABELS: Array<{
    key: keyof PdpAuditHumanConversion;
    label: string;
}> = [
    { key: "conversionOptimization", label: "Conversion Optimization" },
    { key: "contentQuality", label: "Content Quality & Strategy" },
    { key: "visualExperience", label: "Visual Experience" },
    { key: "socialProof", label: "Social Proof & Trust Elements" },
    { key: "seoMetadata", label: "SEO & Metadata" },
    { key: "technicalPerformance", label: "Technical Performance & Accessibility" },
    { key: "mobileFirst", label: "Mobile-First Assessment" },
    { key: "competitorBenchmarking", label: "Competitor Benchmarking" },
];

export const AGENTIC_COMMERCE_DIMENSION_LABELS: Array<{
    key: keyof PdpAuditAgenticCommerce;
    label: string;
}> = [
    { key: "structuredTruth", label: "Structured Truth" },
    { key: "semanticClarity", label: "Semantic Clarity" },
    { key: "explainability", label: "Explainability" },
    { key: "multimodalGrounding", label: "Multimodal Grounding" },
    { key: "executionConfidenceSignals", label: "Execution Confidence Signals" },
    { key: "machineCommerceSignals", label: "Machine Commerce Signals" },
];
